#!/usr/bin/env node

/**
 * SecretSage CLI
 *
 * Terminal-based credential wizard for agent-driven development.
 * The missing OAuth for LLM agents.
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { BANNER } from './utils/banner';
import { initCommand } from './commands/init';
import { addCommand } from './commands/add';
import { listCommand } from './commands/list';
import { getCommand } from './commands/get';
import { grantCommand } from './commands/grant';
import { revokeCommand } from './commands/revoke';
import { configCommand } from './commands/config';
import { removeCommand } from './commands/remove';
import { rotateCommand } from './commands/rotate';
import { exportCommand } from './commands/export';
import { importCommand } from './commands/import';
import { statusCommand } from './commands/status';
import { auditCommand } from './commands/audit';
import { wizardCommand, wizardUICommand } from './commands/wizard';
import { deployCommand } from './commands/deploy';
import {
  backupCodesAddCommand,
  backupCodesListCommand,
  backupCodesShowCommand,
  backupCodesUseCommand,
} from './commands/backup-codes';

const program = new Command();

program
  .name('secretsage')
  .description('Terminal-based credential wizard for agent-driven development')
  .version('0.5.0')
  .addHelpText('beforeAll', BANNER)
  .addHelpText('after', `
${chalk.magenta('Quick Start:')}
  ${chalk.white('secretsage init')}              Initialize vault and generate keypair
  ${chalk.white('secretsage add OPENAI_API_KEY')}  Add a credential
  ${chalk.white('secretsage grant --all')}        Grant all credentials to .env

${chalk.magenta('Agent Usage:')}
  ${chalk.white('secretsage list --json')}        Machine-readable credential list
  ${chalk.white('secretsage grant KEY --yes')}    Non-interactive grant for agents
`);

// Initialize vault
program
  .command('init')
  .description('Initialize SecretSage vault and generate encryption keypair')
  .option('-l, --local', 'Create vault in current directory instead of ~/.secretsage')
  .option('-p, --path <path>', 'Create vault at a custom directory path')
  .option('-y, --yes', 'Skip confirmation prompts')
  .option('--force', 'Overwrite existing configuration')
  .option('--passphrase', 'Protect identity file with a passphrase (scrypt)')
  .action(initCommand);

// Add credential
program
  .command('add <name>')
  .description('Add a credential to the encrypted vault')
  .option('-v, --value <value>', 'Credential value (use "-" for stdin, prompts if not provided)')
  .option('--from-env', 'Import value from existing .env file')
  .option('-q, --quiet', 'Minimal output')
  .action(addCommand);

// List credentials
program
  .command('list')
  .alias('ls')
  .description('List credential names in the vault')
  .option('--json', 'Output as JSON (agent-friendly)')
  .option('-a, --all', 'Include metadata')
  .action(listCommand);

// Get single credential
program
  .command('get <name>')
  .description('Retrieve a single credential value (for scripting)')
  .option('-r, --raw', 'Output only the value (no newline, for piping)')
  .option('--json', 'Output as JSON')
  .option('-q, --quiet', 'Suppress error messages')
  .action(getCommand);

// Grant credentials
program
  .command('grant [names...]')
  .description('Decrypt and write credentials to .env for agent use')
  .option('-a, --all', 'Grant all credentials')
  .option('-y, --yes', 'Skip confirmation (for agents)')
  .option('-e, --env <path>', 'Target .env file path (default: .env in current directory)')
  .option('--no-backup', 'Skip .env backup')
  .action(grantCommand);

// Revoke credentials
program
  .command('revoke [names...]')
  .description('Remove credentials from .env (vault remains intact)')
  .option('-a, --all', 'Revoke all credentials')
  .option('-y, --yes', 'Skip confirmation')
  .action(revokeCommand);

// Config
program
  .command('config')
  .description('View or update SecretSage configuration')
  .option('-s, --show', 'Show current configuration')
  .option('--set <key=value>', 'Set a configuration value')
  .option('--path', 'Show configuration file path')
  .action(configCommand);

// Remove credential from vault
program
  .command('remove <name>')
  .alias('rm')
  .description('Permanently delete a credential from the vault')
  .option('-y, --yes', 'Skip confirmation')
  .option('-q, --quiet', 'Minimal output')
  .action(removeCommand);

// Rotate credential value
program
  .command('rotate <name>')
  .description('Update the value of an existing credential')
  .option('-v, --value <value>', 'New value (use "-" for stdin)')
  .option('-g, --generate <bytes>', 'Generate random value (specify byte length)')
  .option('-r, --reason <reason>', 'Reason for rotation (for audit trail)')
  .option('-y, --yes', 'Skip confirmation')
  .option('-q, --quiet', 'Minimal output')
  .action(rotateCommand);

// Export vault
program
  .command('export')
  .description('Export vault credentials for backup or transfer')
  .option('-o, --output <file>', 'Output file (default: stdout)')
  .option('-f, --format <type>', 'Output format: json, env (default: json)', 'json')
  .option('-k, --keys <names>', 'Comma-separated list of keys to export')
  .option('--encrypted', 'Keep credentials encrypted (for backup)')
  .option('-q, --quiet', 'Minimal output')
  .option('-y, --yes', 'Skip confirmation (requires agent.trustedMode in config)')
  .action(exportCommand);

// Import credentials
program
  .command('import')
  .description('Import credentials from backup or external source')
  .option('-i, --input <file>', 'Input file (default: stdin)')
  .option('-f, --format <type>', 'Input format: json, env (default: json)', 'json')
  .option('--merge', 'Merge with existing vault (default: skip existing)')
  .option('-y, --yes', 'Skip confirmation')
  .option('-q, --quiet', 'Minimal output')
  .action(importCommand);

// Status check
program
  .command('status')
  .description('Show vault status and health check')
  .option('--json', 'Output as JSON (agent-friendly)')
  .action(statusCommand);

// Audit credential history
program
  .command('audit <name>')
  .description('Show rotation history and audit trail for a credential')
  .option('--json', 'Output as JSON (agent-friendly)')
  .action(auditCommand);

// Wizard - agent-human handoff for key entry
program
  .command('wizard')
  .description('Open interactive key entry wizard in new terminal (agent-human handoff)')
  .requiredOption('-k, --keys <keys>', 'Comma-separated list of key names to collect')
  .option('-r, --reason <reason>', 'Reason for rotation (shown in wizard)')
  .option('-t, --timeout <seconds>', 'Timeout in seconds (default: 300)')
  .option('--no-wait', 'Return immediately without waiting for wizard completion')
  .option('-i, --inline', 'Run wizard in current terminal (for headless/SSH environments)')
  .action(wizardCommand);

// Wizard UI - internal command run in spawned terminal
program
  .command('wizard-ui')
  .description('(internal) Wizard UI for terminal')
  .option('-c, --config <path>', 'Path to wizard config file')
  .action(wizardUICommand);

// Deploy secrets to remote
program
  .command('deploy <project>')
  .description('Deploy secrets to a remote server via rsync/SSH')
  .requiredOption('--remote <destination>', 'Remote destination (user@host:/path)')
  .option('-f, --file <path>', 'Local .env file path (default: .env)')
  .option('--restart <command>', 'Post-deploy command to run on remote (e.g., "pm2 restart app")')
  .option('-y, --yes', 'Skip confirmation')
  .action(deployCommand);

// Backup codes - parent command
const backupCodes = program
  .command('backup-codes')
  .alias('bc')
  .description('Manage 2FA backup/recovery codes');

backupCodes
  .command('add <service>')
  .description('Store backup codes for a service')
  .option('-a, --account <email>', 'Account identifier (for multiple accounts)')
  .option('-q, --quiet', 'Minimal output')
  .action(backupCodesAddCommand);

backupCodes
  .command('list')
  .alias('ls')
  .description('List services with stored backup codes')
  .option('--json', 'Output as JSON')
  .action(backupCodesListCommand);

backupCodes
  .command('show <service>')
  .description('Display backup codes for a service')
  .option('-a, --account <email>', 'Account identifier')
  .option('-y, --yes', 'Skip confirmation')
  .action(backupCodesShowCommand);

backupCodes
  .command('use <service>')
  .description('Mark a backup code as used')
  .option('-a, --account <email>', 'Account identifier')
  .option('-i, --index <number>', 'Code number to mark as used')
  .action(backupCodesUseCommand);

// Parse and run
program.parse(process.argv);

// Show help if no command
if (!process.argv.slice(2).length) {
  program.outputHelp();
}
